import { Add, Delete, FastForward, FastRewind, Forward30, Remove, Replay30 } from '@mui/icons-material'
import { Box, Button, Dialog, DialogTitle, Stack, SxProps, Theme, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import { ReactElement, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useApp } from '../hooks/useApp'
import { callAxios } from '../hooks/useAxios'
import { Subscription } from '../types/models'
import { IconRowSelector } from './atoms/IconRowSelector'

export interface SubscriptionInfoProps {
  subscription: Subscription
  onUpdate?: () => void
  sx?: SxProps<Theme>
}

export function SubscriptionInfo(props: SubscriptionInfoProps): ReactElement {
  const { sx, subscription, onUpdate } = props

  const [deletable, setDeletable] = useState<Subscription|null>(null)
  const { t } = useTranslation()
  const app = useApp()

  // Change the expiration.
  const onAdjust = async (days: number) => {
    const time = DateTime.fromISO(subscription.expires).plus({ days })
    const result = await callAxios<Subscription>({
      url: `/subscriptions/${subscription.id}`,
      method: 'PATCH',
    }, { expires: time })

    if (result.success) {
      onUpdate && onUpdate()
    } else {
      app.addError(result)
    }
  }

  // Remove the subscription.
  const onDelete = async () => {
    const result = await callAxios({
      url: `/subscriptions/${subscription.id}`,
      method: 'DELETE',
    })

    if (result.success) {
      onUpdate && onUpdate()
    } else {
      app.addError(result)
    }

    setDeletable(null)
  }

  return <Box sx={{
    ...sx
  }}>
    <Box sx={{ mb: 1 }} color="secondary.main" data-cy={`box-Package-${subscription.package.code}`}>
      {t(`Package-${subscription.package.code}`)}

      <IconRowSelector
        icons={[
          {
            icon: <FastRewind/>,
            action: () => onAdjust(-365),
            title: t('Reduce 365 days from the subscription')
          },
          {
            icon: <Replay30/>,
            action: () => onAdjust(-30),
            title: t('Reduce 30 days from the subscription')
          },
          {
            icon: <Remove/>,
            action: () => onAdjust(-1),
            title: t('Reduce one day from the subscription')
          },
          {
            icon: <Add/>,
            action: () => onAdjust(1),
            title: t('Add one day the subscription')
          },
          {
            icon: <Forward30/>,
            action: () => onAdjust(30),
            title: t('Add 30 days the subscription')
          },
          {
            icon: <FastForward/>,
            action: () => onAdjust(365),
            title: t('Add 365 days the subscription')
          },
          {
            icon: <Delete/>,
            action: () => setDeletable(subscription),
            title: t('Cancel the subscription'),
            sx: { color: 'error.main' }
          },
        ]}
      />
    </Box>

    <Trans>Expires</Trans>:<> </>
      <Typography component="span" sx={{ color: subscription.expired ? 'error.light' : 'inherit' }}>
        {DateTime.fromISO(subscription.expires).toLocaleString(DateTime.DATETIME_FULL)}
      </Typography>
    <br/>

    <Trans>Area</Trans>:<> </>
      <Typography component="span">{subscription.package.area.name}</Typography>
    {
      subscription.package.features.map(f => <Box key={f} sx={{ ml: 3, mt: 1 }}>{subscription.expired ? '➖' : '🍀'}<> </>{t(`Feature-${f}`)}</Box>)
    }

    {deletable &&
      <Dialog open={!!deletable} onClose={() => setDeletable(null)}>
          <DialogTitle>{deletable && t(`Package-${deletable.package.code}`)}</DialogTitle>
          <Box sx={{ p: 2 }}>
            <Trans>Are you sure that you want remove this package from the user?</Trans>
            <Stack direction="row" spacing={2} sx={{ m: 2 }}>
              <Button color="primary" onClick={() => setDeletable(null)}><Trans>Cancel</Trans></Button>
              <Button color="secondary" onClick={onDelete}><Trans>OK</Trans></Button>
            </Stack>
          </Box>
      </Dialog>
      }
  </Box>
}
